import { useProjectApiClient } from "@api/project-api/use-project-api-client";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { generateGUID } from "@faro-lotv/foundation";
import { IElementType, IElementTypeHint } from "@faro-lotv/ielement-types";
import { createMutationAddMarkupField, createMutationDateTimeMarkupField, createMutationDeleteElement } from "@faro-lotv/service-wires";
import { AnnotationEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
// TODO: This function is copied from lotv-monorepo as it is not available in the public API.
// We have to remove it and import it from there later: https://faro01.atlassian.net/browse/ST-1784
/** Creates mutation for iElement markup due date */
function createEditDueDateMutation({
  markupElement,
  newDueDate,
  previousValueId,
  dueDateTemplateId
}) {
  if (newDueDate) {
    // Edit previous value if it exists
    if (previousValueId) {
      return createMutationDateTimeMarkupField(previousValueId, newDueDate);
    }

    // Create a new node if it did not exists
    return createMutationAddMarkupField(markupElement.id, {
      id: generateGUID(),
      childrenIds: null,
      name: "Due Date",
      parentId: markupElement.id,
      rootId: markupElement.rootId,
      templateId: dueDateTemplateId,
      type: IElementType.dateTimeMarkupField,
      typeHint: IElementTypeHint.markupIssueDueDate,
      value: newDueDate.toISOString()
    });
  } else if (previousValueId) {
    // Remove the value node if not needed anymore
    return createMutationDeleteElement(previousValueId);
  }
}
/** A hook that gathers all the logic for updating the due date */
export function useUpdateDueDate({
  projectId
}) {
  const {
    handleErrorWithToast
  } = useErrorContext();
  const projectApiClient = useProjectApiClient({
    projectId
  });
  const {
    trackEvent
  } = useTrackEvent();
  async function removeDueDate(markup) {
    // Check if the due date ID exists before attempting deletion
    if (!markup.dueDate?.id) {
      return false;
    }
    try {
      trackEvent({
        name: AnnotationEvents.editAnnotation,
        props: {
          property: "due-date",
          isValueEmpty: true
        }
      });
      const mutation = createMutationDeleteElement(markup.dueDate.id);

      // Delete the dueDate element using projectApiClient
      await projectApiClient.applyMutations([mutation]);
      return true;
    } catch (error) {
      handleErrorWithToast({
        id: `deleteAnnotationDueDate-${Date.now().toString()}`,
        title: "Could not delete the annotation due date. Please try again",
        error
      });
      return false;
    }
  }

  /** Handler for when user changes the due date. Can be undefined if the due date is removed */
  async function onDueDateChange(newDate, markup_0, dueDateTemplateId) {
    try {
      trackEvent({
        name: AnnotationEvents.editAnnotation,
        props: {
          property: "due-date",
          isValueEmpty: false
        }
      });
      const mutation_0 = createEditDueDateMutation({
        markupElement: markup_0,
        newDueDate: newDate,
        previousValueId: markup_0.dueDate?.id,
        dueDateTemplateId
      });
      if (!mutation_0) {
        throw Error("There was an error to update the due date");
      }
      await projectApiClient.applyMutations([mutation_0]);
      return mutation_0;
    } catch (error_0) {
      handleErrorWithToast({
        id: `updateAnnotationDueDate-${Date.now().toString()}`,
        title: "Could not change annotation due date. Please try again",
        error: error_0
      });
    }
  }
  return {
    removeDueDate,
    onDueDateChange
  };
}